import { Section } from 'components/Rdc/Rdc'
import { RdcSectionTransportMode } from 'components/Rdc/RdcSection/RdcSection'
import { SectionBreakdownItem } from 'components/Rdc/RdcSection/SectionBreakdownList/SectionBreakdownList'
import { RouteListItemSection, RouteListItemTransportMode } from 'components/RouteListItem'
import { TransportModesPanelMode } from 'components/RoutePlanner/TransportModesPanel'
import {
  ElevationInfo,
  ElevationSection,
  StatePolylineColor,
  StateRoute,
  StateRouteAttributions,
  StateRouteSection,
  StateRouteTransportGroup,
  StateSectionAction,
  StateSectionArrival,
  StateSectionColor,
  StateSectionDeparture,
  StateSectionIntermediateStop,
  StateSectionTransportMode,
  StateSectionTravelSummary,
} from 'data/model/routePlanner'
import { addSeconds, differenceInSeconds } from 'date-fns'
import {
  IntermodalResponseAction,
  IntermodalResponseArrivalDeparture,
  IntermodalResponseIntermediateStop,
  IntermodalResponseRoute,
  IntermodalResponseRouteSection,
  IntermodalResponseSectionSummary,
  IntermodalTransport,
} from 'map/intermodalApi'
import {
  RoutingResponseRoute,
  RoutingResponseRouteSection,
  RoutingResponseSectionAction,
  RoutingResponseSectionArrivalDeparture,
  RoutingResponseSectionTravelSummary,
} from 'map/routingService'
import { getContrastForegroundColor, isWhite } from 'utils/transport'

export const fromStateRouteSectionToRouteListItemSection = (
  section: StateRouteSection
): RouteListItemSection => ({
  colors: {
    chip: section.color.main,
    icon: section.color.text,
    text: section.color.text,
  },
  delay: section.delay,
  departsIn: section.departure.in,
  duration: section.travelSummary.duration,
  mode: fromStateSectionTransportModeToRouteListItemTransportMode(section.mode),
  title: section.name,
})

export const fromStateSectionTransportModeToRouteListItemTransportMode = (
  mode: StateSectionTransportMode
): RouteListItemTransportMode => {
  switch (mode) {
    case StateSectionTransportMode.Car:
      return RouteListItemTransportMode.Car
    case StateSectionTransportMode.Pedestrian:
      return RouteListItemTransportMode.Pedestrian
    case StateSectionTransportMode.Bike:
      return RouteListItemTransportMode.Bicycle
    case StateSectionTransportMode.Subway:
      return RouteListItemTransportMode.Subway
    case StateSectionTransportMode.Train:
      return RouteListItemTransportMode.Train
    case StateSectionTransportMode.Tram:
      return RouteListItemTransportMode.Tram
    case StateSectionTransportMode.Bus:
      return RouteListItemTransportMode.Bus
    case StateSectionTransportMode.Ferry:
      return RouteListItemTransportMode.Ferry
    default:
      return RouteListItemTransportMode.Unknown
  }
}
export const toRdcSection = (section: StateRouteSection): Section => {
  const ptStops =
    section.intermediateStops !== undefined
      ? [
          ...section.intermediateStops,
          {
            name: section.arrival.name,
            time: section.arrival.time,
          } as StateSectionIntermediateStop,
        ]
      : []
  const breakdownItems = section.actions
    ? section.actions.map(
        (action: StateSectionAction): SectionBreakdownItem => ({
          distance: action.length,
          icon: action.icon,
          title: action.instruction,
        })
      )
    : ptStops.map(
        (stop: StateSectionIntermediateStop): SectionBreakdownItem => ({
          departure: new Date(stop.time),
          title: stop.name,
        })
      )
  return {
    breakdownItems: breakdownItems !== undefined ? breakdownItems : [],
    color: {
      background: section.color.main,
      icon: section.color.text,
      text: section.color.text,
    },
    delay: section.delay,
    departure: {
      place: section.departure.from.place,
      platform: section.departure.from.platform,
      time: new Date(section.departure.time),
    },
    distance: section.travelSummary.length,
    duration: section.travelSummary.duration,
    headsign: section.headsign,
    mode: toRdcSectionTransportMode(section.mode),
    name: section.name,
  }
}
export const toRdcSectionTransportMode = (
  mode: StateSectionTransportMode
): RdcSectionTransportMode => {
  switch (mode) {
    case StateSectionTransportMode.Car:
      return RdcSectionTransportMode.Car
    case StateSectionTransportMode.Pedestrian:
      return RdcSectionTransportMode.Pedestrian
    case StateSectionTransportMode.Bike:
      return RdcSectionTransportMode.Bike
    case StateSectionTransportMode.Subway:
      return RdcSectionTransportMode.Underground
    case StateSectionTransportMode.Train:
      return RdcSectionTransportMode.Train
    case StateSectionTransportMode.Tram:
      return RdcSectionTransportMode.Tram
    case StateSectionTransportMode.Bus:
      return RdcSectionTransportMode.Bus
    case StateSectionTransportMode.Ferry:
      return RdcSectionTransportMode.Ferry
    default:
      return RdcSectionTransportMode.Unknown
  }
}

export const fromTransportModesPanelModeToRoutingServiceModes = (
  mode: TransportModesPanelMode
): string => {
  switch (mode) {
    case TransportModesPanelMode.Car:
      return 'car'
    case TransportModesPanelMode.Walk:
      return 'pedestrian'
    case TransportModesPanelMode.Bike:
      return 'bicycle'
    default:
      return 'unknown'
  }
}
export const fromTransportModesPanelModeToStateRouteTransportGroup = (
  mode: TransportModesPanelMode
): StateRouteTransportGroup => {
  switch (mode) {
    case TransportModesPanelMode.Car:
      return StateRouteTransportGroup.Car
    case TransportModesPanelMode.Walk:
      return StateRouteTransportGroup.Walk
    case TransportModesPanelMode.PublicTransport:
      return StateRouteTransportGroup.PublicTransport
    case TransportModesPanelMode.Bike:
      return StateRouteTransportGroup.Bike
    case TransportModesPanelMode.Motorcycle:
      return StateRouteTransportGroup.Motorcycle
    case TransportModesPanelMode.Taxi:
      return StateRouteTransportGroup.Taxi
    default:
      return StateRouteTransportGroup.Unknown
  }
}

/**
 * Decodes the specified polyline, converts it to the LineString, get array of triple coord values and return
 * the elevations.
 * @param { string } polyline // polyline attribute from section
 * @returns { number[] } // array of elevation values
 */
const getElevationsByPolyline = (polyline: string): number[] => {
  return H.geo.LineString.fromFlexiblePolyline(polyline)
    .getLatLngAltArray()
    .filter((_n, i) => (i + 1) % 3 === 0)
}

/**
 * Gets the elevation info for a given array of elevations, just comparing the consecutive values.
 * @param { number[] } elevations // Array of elevation values.
 * @returns {{ elevation_down: string, elevation_up: number }} // how much it increases or decreases.
 */
const getElevationInfo = (elevations: number[]): ElevationInfo => {
  let eUP = 0
  let eDOWN = 0
  for (let i = 0; i < elevations.length - 1; i++) {
    const first = elevations[i]
    const second = elevations[i + 1]
    if (first - second > 0) {
      eDOWN += first - second
    }
    if (first - second < 0) {
      eUP += -(first - second)
    }
  }
  return { elevation_down: eDOWN, elevation_up: eUP }
}

export const fromRoutingResponseRouteToStateRoute = (route: RoutingResponseRoute): StateRoute => {
  const sections: StateRouteSection[] = route.sections.reduce(
    (acc: StateRouteSection[], section: RoutingResponseRouteSection) => {
      return [
        ...acc,
        {
          actions: section.actions?.map((action) =>
            fromRoutingResponseSectionActionToStateSectionAction(action)
          ),
          arrival: fromRoutingResponseSectionArrivalDepartureToStateSectionArrival(section.arrival),
          color: fromRoutingResponseTransportModeToStateSectionColor(section.transport.mode),
          departure: fromRoutingResponseSectionArrivalDepartureToStateSectionDeparture(
            section.departure
          ),
          id: section.id,
          mode: fromRoutingResponseTransportModeToStateSectionTransportMode(section.transport.mode),
          name: undefined,
          polyline: {
            color: fromRoutingResponseTransportModeToStatePolylineColor(section.transport.mode),
            value: section.polyline,
          },
          travelSummary: fromRoutingResponseSectionTravelSummaryToStateSectionTravelSummary(
            section.travelSummary
          ),
        },
      ] as StateRouteSection[]
    },
    []
  )
  const distance = route.sections.reduce(
    (acc: number, section) => acc + section.travelSummary.length,
    0
  )
  const duration = route.sections.reduce((acc, section) => acc + section.travelSummary.duration, 0)
  const arrival = new Date(route.sections[route.sections.length - 1].arrival.time).toISOString()
  const departure = new Date(route.sections[0].departure.time).toISOString()
  const delay = route.sections.find((section) => section.transport.mode === 'car')
    ? route.sections.reduce(
        (acc: number, section) =>
          acc +
          (section.travelSummary.baseDuration !== undefined &&
          section.travelSummary.duration !== undefined
            ? section.travelSummary.duration - section.travelSummary.baseDuration
            : 0),
        0
      )
    : undefined

  // Get the elevation info only for 'bicycle' sections:
  const elevations: number[] = route.sections
    .filter(
      (section) => section.transport.mode === 'bicycle' || section.transport.mode === 'pedestrian'
    )
    .reduce((acc: number[], section: RoutingResponseRouteSection) => {
      return acc.concat(getElevationsByPolyline(section.polyline))
    }, [])
  const elevationSections: ElevationSection[] = route.sections.map((section) => {
    return {
      elevation_color: fromRoutingResponseTransportModeToStateSectionColor(section.transport.mode)
        .main,
      elevation_data: getElevationsByPolyline(section.polyline),
    }
  })
  const elevationInfo = getElevationInfo(elevations)

  return {
    arrival,
    delay,
    departure,
    distance,
    duration,
    elevationInfo,
    elevationSections,
    elevations,
    id: route.id,
    sections,
  }
}

const fromIntermodalResponseTransportModeToStateSectionTransportMode = (
  mode: string
): StateSectionTransportMode | undefined => {
  switch (mode) {
    case 'pedestrian':
      return StateSectionTransportMode.Pedestrian
    case 'bicycle':
      return StateSectionTransportMode.Bike
    case 'highSpeedTrain':
    case 'cityTrain':
    case 'intercityTrain':
    case 'interRegionalTrain':
    case 'regionalTrain':
      return StateSectionTransportMode.Train
    case 'subway':
      return StateSectionTransportMode.Subway
    case 'lightRail':
    case 'monorail':
    case 'inclined':
    case 'aerial':
      return StateSectionTransportMode.Tram
    case 'bus':
    case 'busRapid':
      return StateSectionTransportMode.Bus
    case 'car':
      return StateSectionTransportMode.Car
    case 'ferry':
      return StateSectionTransportMode.Ferry
  }
}

export const fromIntermodalResponseRouteToStateRoute = (
  route: IntermodalResponseRoute
): StateRoute => {
  const sections: StateRouteSection[] = route.sections
    .filter(
      (section) =>
        !section.id.includes('-dep') &&
        !section.id.includes('-arr') &&
        !section.id.includes('-trailing') &&
        !section.spans?.filter((span) => span.walkAttributes?.includes('indoor'))
    )
    .reduce((acc: StateRouteSection[], section: IntermodalResponseRouteSection) => {
      const transportMode = fromIntermodalResponseTransportModeToStateSectionTransportMode(
        section.transport.mode
      )
      section.departure.delay =
        section.departure.delay !== null &&
        section.departure.delay !== undefined &&
        section.departure?.delay < 0
          ? 0
          : section.departure.delay

      return [
        ...acc,
        {
          actions: section.actions?.map((action) =>
            fromIntermodalResponseActionToStateSectionAction(action)
          ),
          arrival: fromIntermodalResponseArrivalDepartureToStateSectionArrival(section.arrival),
          color: fromIntermodalResponseTransportToStateSectionColor(section.transport),
          delay: filterSectionDelay(section.departure.delay),
          departure: fromIntermodalResponseArrivalDepartureToStateSectionDeparture(
            section.departure
          ),
          headsign: section.transport.headsign,
          id: section.id,
          intermediateStops: section.intermediateStops?.map(
            (stop: IntermodalResponseIntermediateStop) =>
              fromIntermodalResponseIntermediateStopToStateSectionIntermediateStop(stop)
          ),
          mode: transportMode || StateSectionTransportMode.Car,
          name: section.transport.shortName,
          polyline: {
            color: fromIntermodalResponseTransportToPolylineColor(section.transport),
            value: section.polyline,
          },
          travelSummary: fromIntermodalResponseSectionTravelSummaryToStateSectionTravelSummary(
            section.travelSummary
          ),
        },
      ] as StateRouteSection[]
    }, [])

  const attributions: StateRouteAttributions[] = route.sections
    .filter((section) => section.type === 'transit')
    .reduce((acc: StateRouteAttributions[], section: IntermodalResponseRouteSection) => {
      const ids = section.attributions?.map((attribution) => attribution.id) || []
      if (section.attributions && acc.every((attribution) => !ids.includes(attribution.id))) {
        return [...acc, ...section.attributions] as StateRouteAttributions[]
      }
      return acc
    }, [])
    .filter((attributions) => attributions.type === 'disclaimer')
  const distance = route.sections.reduce(
    (acc: number, section) => acc + section.travelSummary.length,
    0
  )
  const delay = route.sections.reduce(
    (acc, section) => (section.departure.delay === undefined ? acc : acc + section.departure.delay),
    0
  )
  const departure = new Date(route.sections[0].departure.time).toISOString()
  const arrival = new Date(route.sections[route.sections.length - 1].arrival.time).toISOString()
  const duration = Math.abs(differenceInSeconds(new Date(arrival), new Date(departure)))

  return {
    arrival,
    attributions,
    delay,
    departure,
    distance,
    duration,
    id: route.id,
    sections,
  }
}

const fromIntermodalResponseTransportToStateSectionColor = (
  transport: IntermodalTransport
): StateSectionColor => {
  if (transport.mode === 'pedestrian') {
    return {
      border: '#08a8a4',
      main: '#08a8a4',
      text: '#ffffff',
    }
  }

  if (transport.mode === 'bicycle') {
    return {
      border: '#097995',
      main: '#097995',
      text: '#ffffff',
    }
  }

  if (transport.color === undefined) {
    return {
      border: '#101723f2',
      main: ' #6A7483',
      text: '#ffffff',
    }
  }

  const transportColor = isWhite(transport.color) ? '#808080' : transport.color
  return {
    border: transportColor,
    main: transportColor,
    text: getContrastForegroundColor(transportColor),
  }
}

const fromIntermodalResponseTransportToPolylineColor = (
  transport: IntermodalTransport
): StatePolylineColor => {
  if (transport.mode === 'pedestrian') {
    return {
      main: '#08a8a4',
      stroke: shadeColor('#08a8a4', -40),
    }
  }
  if (transport.mode === 'bicycle') {
    return {
      main: '#097995',
      stroke: shadeColor('#097995', -40),
    }
  }

  if (transport?.color === undefined) {
    return {
      main: '#6A7483',
      stroke: shadeColor('#6A7483', -40),
    }
  }

  const transportColor = isWhite(transport.color) ? '#808080' : transport.color
  return {
    main: transportColor,
    stroke: shadeColor(transportColor, -40),
  }
}

const fromIntermodalResponseActionToStateSectionAction = (
  action?: IntermodalResponseAction
): StateSectionAction | undefined => {
  if (action === undefined) {
    return undefined
  }

  const icon = getManeuverIcon(action.action, action.direction, action.exit)

  return {
    direction: action.direction,
    duration: action.duration,
    eta: action.eta,
    exit: action.exit,
    icon,
    instruction: action.instruction,
    length: action.length,
    name: action.action,
    offset: action.offset,
  }
}

const getManeuverIcon = (action: string, direction?: string, exitCode?: number): string => {
  const directionStr = direction !== undefined ? direction : ''
  switch (action) {
    case 'exit':
    case 'ramp':
    case 'turn':
      return `${directionStr}-${action}`
    case 'continue':
      return 'continue-on'
    case 'bear':
      return `${action}-${directionStr}`
    case 'roundaboutExit':
      return `${directionStr}-roundabout-exit${exitCode !== undefined ? exitCode : ''}`
    case 'uTurn':
      return `${directionStr}-u-turn`
    default:
      return action
  }
}

const fromIntermodalResponseArrivalDepartureToStateSectionArrival = (
  arrival: IntermodalResponseArrivalDeparture
): StateSectionArrival => ({
  at: {
    lat: arrival.place.location.lat,
    lng: arrival.place.location.lng,
  },
  name: arrival.place.name,
  time: new Date(arrival.time).toISOString(),
})

const fromIntermodalResponseArrivalDepartureToStateSectionDeparture = (
  departure: IntermodalResponseArrivalDeparture
): StateSectionDeparture => ({
  from: {
    location: {
      lat: departure.place.location.lat,
      lng: departure.place.location.lng,
    },
    place: departure.place.name,
    platform: departure.place.platform,
  },
  in: getSecondsBeforeDeparture(new Date(departure.time), departure.delay),
  time: new Date(departure.time).toISOString(),
})

const getSecondsBeforeDeparture = (time: Date, delay?: number): number => {
  if (delay !== undefined) {
    const departureWithDelay = addSeconds(time, delay)
    return differenceInSeconds(departureWithDelay, Date.now())
  }

  return differenceInSeconds(time, Date.now())
}

const filterSectionDelay = (delay?: number): number | undefined =>
  delay !== undefined && delay < 0 ? 0 : delay

const fromIntermodalResponseSectionTravelSummaryToStateSectionTravelSummary = (
  travelSummary: IntermodalResponseSectionSummary
): StateSectionTravelSummary => ({
  baseDuration: travelSummary.baseDuration,
  duration: travelSummary.duration,
  length: travelSummary.length,
})

const fromIntermodalResponseIntermediateStopToStateSectionIntermediateStop = (
  intermediateStop: IntermodalResponseIntermediateStop
): StateSectionIntermediateStop => ({
  location: {
    lat: intermediateStop.departure.place.location.lat,
    lng: intermediateStop.departure.place.location.lng,
  },
  name: intermediateStop.departure.place.name,
  time: new Date(intermediateStop.departure.time).toISOString(),
})

const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16)
  let G = parseInt(color.substring(3, 5), 16)
  let B = parseInt(color.substring(5, 7), 16)

  R = Math.round((R * (100 + percent)) / 100)
  G = Math.round((G * (100 + percent)) / 100)
  B = Math.round((B * (100 + percent)) / 100)

  R = R < 255 ? R : 255
  G = G < 255 ? G : 255
  B = B < 255 ? B : 255

  const RR = R.toString(16).length === 1 ? '0' + R.toString(16) : R.toString(16)
  const GG = G.toString(16).length === 1 ? '0' + G.toString(16) : G.toString(16)
  const BB = B.toString(16).length === 1 ? '0' + B.toString(16) : B.toString(16)

  return '#' + RR + GG + BB
}

const fromRoutingResponseSectionActionToStateSectionAction = (
  action: RoutingResponseSectionAction
): StateSectionAction => {
  const icon = getManeuverIcon(action.action, action.direction, action.exit)

  return {
    direction: action.direction,
    duration: action.duration,
    eta: action.eta,
    exit: action.exit,
    icon,
    instruction: action.instruction,
    length: action.length,
    name: action.action,
    offset: action.offset,
  }
}

const fromRoutingResponseTransportModeToStateSectionTransportMode = (
  mode: string
): StateSectionTransportMode => {
  switch (mode) {
    case 'car':
      return StateSectionTransportMode.Car
    case 'pedestrian':
      return StateSectionTransportMode.Pedestrian
    case 'bicycle':
      return StateSectionTransportMode.Bike
    case 'carShuttleTrain':
      return StateSectionTransportMode.Train
    case 'ferry':
      return StateSectionTransportMode.Ferry
    default:
      return StateSectionTransportMode.Unknown
  }
}

const fromRoutingResponseTransportModeToStateSectionColor = (mode: string): StateSectionColor => {
  switch (mode) {
    case 'car':
      return {
        border: shadeColor('#126ef8', -40),
        main: '#126ef8',
        text: '#ffffff',
      }
    case 'pedestrian':
      return {
        border: shadeColor('#08a8a4', -40),
        main: '#08a8a4',
        text: '#ffffff',
      }
    case 'bicycle':
      return {
        border: shadeColor('#097995', -40),
        main: '#097995',
        text: '#ffffff',
      }
    default:
      return {
        border: shadeColor('#6A7483', -40),
        main: '#6A7483',
        text: '#ffffff',
      }
  }
}

const fromRoutingResponseTransportModeToStatePolylineColor = (mode: string): StatePolylineColor => {
  switch (mode) {
    case 'car':
      return {
        main: '#4791FF',
        stroke: shadeColor('#4791FF', -40),
      }
    case 'pedestrian':
      return {
        main: '#08a8a4',
        stroke: shadeColor('#08a8a4', -40),
      }
    case 'bicycle':
      return {
        main: '#097995',
        stroke: shadeColor('#097995', -40),
      }
    default:
      return {
        main: '#07173199',
        stroke: shadeColor('#07173199', -40),
      }
  }
}

const fromRoutingResponseSectionArrivalDepartureToStateSectionArrival = (
  arrival: RoutingResponseSectionArrivalDeparture
): StateSectionArrival => ({
  at: { lat: arrival.place.location.lat, lng: arrival.place.location.lng },
  time: new Date(arrival.time).toISOString(),
})

const fromRoutingResponseSectionArrivalDepartureToStateSectionDeparture = (
  departure: RoutingResponseSectionArrivalDeparture
): StateSectionDeparture => ({
  from: {
    location: {
      lat: departure.place.location.lat,
      lng: departure.place.location.lng,
    },
  },
  time: new Date(departure.time).toISOString(),
})

const fromRoutingResponseSectionTravelSummaryToStateSectionTravelSummary = (
  travelSummary: RoutingResponseSectionTravelSummary
): StateSectionTravelSummary => ({
  baseDuration: travelSummary.baseDuration,
  duration: travelSummary.duration,
  length: travelSummary.length,
})
